import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import SearchResultPickerModal from './SearchResultPickerModal';
import VoiceHelpAnswerModal from './VoiceHelpAnswerModal';
import VoiceInputWaveform from './VoiceInputWaveform';
import useMediaResolverHealth from '../useMediaResolverHealth';
import useVoiceCapture from '../useVoiceCapture';
import { kickoffMicrophoneAccess } from '../microphoneAccess';
import { submitVoiceCommand } from '../voiceCommandClient';
import { executeVoiceCommand } from '../voiceCommandExecutor';
import { buildVoiceCatalogs, formatVoiceCommandFeedback } from '../voiceCommandUtils';
import { primaryArtist } from '../tuneBibliographicUtils';
import { isAndroidApp } from '../platformUtils';
import { OFFLINE_MESSAGE, isNavigatorOffline } from '../offlineNetwork';

function MicIcon() {
  return (
    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="microphone" className="svg-inline--fa fa-microphone fa-w-11" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 512" style={{ width: '55%', height: '55%' }}>
      <path fill="currentColor" d="M176 352c53.02 0 96-42.98 96-96V96c0-53.02-42.98-96-96-96S80 42.98 80 96v160c0 53.02 42.98 96 96 96zm160-160h-16c-8.84 0-16 7.16-16 16v48c0 74.8-64.49 134.82-140.79 127.38C96.71 376.89 48 317.11 48 250.3V208c0-8.84-7.16-16-16-16H16c-8.84 0-16 7.16-16 16v40.16c0 89.64 63.97 169.55 152 181.69V464H96c-8.84 0-16 7.16-16 16v16c0 8.84 7.16 16 16 16h160c8.84 0 16-7.16 16-16v-16c0-8.84-7.16-16-16-16h-56v-33.77C285.71 418.47 352 344.9 352 256v-48c0-8.84-7.16-16-16-16z" />
    </svg>
  );
}

const VOICE_COMMAND_TIMEOUT_MS = 120000
const EMPTY_HELP_LINKS = []

export default function VoiceCommandButton(props) {
  const { available: resolverAvailable, features } = useMediaResolverHealth();
  const [processing, setProcessing] = useState(false);
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerTitle, setPickerTitle] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [showHelpAnswer, setShowHelpAnswer] = useState(false);
  const [helpAnswer, setHelpAnswer] = useState(null);

  const abortRef = useRef(null);
  const disambiguateResolverRef = useRef(null);

  const whisperAvailable = Boolean(resolverAvailable && features.whisper);
  const preferNative = isAndroidApp();

  useEffect(function() {
    return function() {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  function setKeyboardBlocked(blocked) {
    if (props.setBlockKeyboardShortcuts) props.setBlockKeyboardShortcuts(blocked);
  }

  function pausePlaybackForVoice() {
    const mediaController = props.mediaController;
    if (!mediaController || !mediaController.pause) return;
    if (mediaController.isPlaying || mediaController.isLoading) {
      mediaController.pause();
    }
  }

  async function runVoiceCommand(submitOptions) {
    if (isNavigatorOffline()) {
      toast.info(OFFLINE_MESSAGE);
      return;
    }
    if (!resolverAvailable) {
      toast.info('Media resolver is required to run voice commands');
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let timedOut = false;
    const timeoutId = setTimeout(function() {
      timedOut = true;
      controller.abort();
    }, VOICE_COMMAND_TIMEOUT_MS);

    setProcessing(true);
    setKeyboardBlocked(true);

    try {
      const catalogs = buildVoiceCatalogs(props.tunebook);
      const result = await submitVoiceCommand(Object.assign({
        books: catalogs.books,
        tags: catalogs.tags,
        mode: props.voiceMode || 'playback',
        accessToken: props.token && props.token.access_token,
        signal: controller.signal,
      }, submitOptions));

      const speakFeedback = typeof window !== 'undefined'
        && localStorage.getItem('bookstorage_announcesong') === 'true';

      const transcript = result && result.transcript ? result.transcript : '';

      await executeVoiceCommand(result, {
        tunes: props.tunes,
        tunebook: props.tunebook,
        setFilter: props.setFilter,
        setCurrentTuneBook: props.setCurrentTuneBook,
        setTagFilter: props.setTagFilter,
        setGroupBy: props.setGroupBy,
        setCurrentTune: props.setCurrentTune,
        mediaController: props.mediaController,
        voiceMode: props.voiceMode || 'playback',
        speakFeedback: speakFeedback,
        onFeedback: function(message) {
          toast.info(formatVoiceCommandFeedback(transcript, message));
        },
        onHelpAnswer: function(payload) {
          setHelpAnswer(payload || null);
          setShowHelpAnswer(true);
        },
        onDisambiguate: function(candidates, query) {
          return new Promise(function(resolve) {
            disambiguateResolverRef.current = resolve;
            setPickerItems(candidates.map(function(tune) {
              return {
                id: tune.id,
                title: tune.name,
                artist: primaryArtist(tune),
              };
            }));
            setPickerTitle('Choose a tune for "' + query + '"');
            setShowPicker(true);
          });
        },
      });
    } catch (error) {
      if (error && error.name === 'AbortError') {
        if (timedOut) {
          toast.error(isNavigatorOffline()
            ? OFFLINE_MESSAGE
            : 'Voice command timed out — check that the media resolver is running');
        }
        return;
      }
      toast.error(error && error.message ? error.message : 'Voice command failed');
    } finally {
      clearTimeout(timeoutId);
      setProcessing(false);
      setKeyboardBlocked(false);
      abortRef.current = null;
    }
  }

  function processCapture(capture) {
    if (!capture) return;
    if (capture.kind === 'transcript') {
      runVoiceCommand({ transcript: capture.transcript });
      return;
    }
    if (capture.kind === 'audio' && capture.blob) {
      runVoiceCommand({
        blob: capture.blob,
        fileName: 'voice-command.webm',
      });
    }
  }

  const {
    recordingState,
    analyserNode,
    inputLevel,
    isTapMode,
    nativeReady,
    nativeChecked,
    usingNativeStt,
    handleTapPointerDown,
    handlePointerDown,
    handlePointerUp,
    handlePointerCancel,
    microphoneErrorMessage,
  } = useVoiceCapture({
    enabled: preferNative || whisperAvailable,
    preferNative: preferNative,
    audioFallbackEnabled: whisperAvailable,
    onBeforeStart: pausePlaybackForVoice,
    onCaptureStopping: function() {
      setProcessing(true);
    },
    onEmptyCapture: function() {
      setProcessing(false);
    },
    onCaptureReady: processCapture,
    onNativeSoftFail: function(reason) {
      if (whisperAvailable) {
        toast.info("Couldn't catch that (" + (reason || 'empty') + ") — tap again for server STT");
      } else {
        toast.info('No speech recognised' + (reason ? ' (' + reason + ')' : ''));
      }
    },
    onError: function(error) {
      toast.error(microphoneErrorMessage(error));
    },
    onHoldModeShortTap: function() {
      toast.warning('Hold the mic button down while speaking');
    },
    setKeyboardBlocked: setKeyboardBlocked,
  });

  function onMicPointerDown(event) {
    if (isTapMode) {
      // Do not open getUserMedia while native SpeechRecognizer is active —
      // WebView mic capture steals the input and recognition fails.
      if (usingNativeStt) {
        handleTapPointerDown(event);
      } else {
        const streamPromise = kickoffMicrophoneAccess();
        handleTapPointerDown(event, streamPromise);
      }
    } else {
      handlePointerDown(event);
    }
  }

  function handlePickerSelect(item) {
    setShowPicker(false);
    const resolver = disambiguateResolverRef.current;
    disambiguateResolverRef.current = null;
    if (!resolver) return;
    const tune = item && item.id ? props.tunes[item.id] : null;
    resolver(tune || null);
    setPickerItems([]);
  }

  function handlePickerHide() {
    setShowPicker(false);
    const resolver = disambiguateResolverRef.current;
    disambiguateResolverRef.current = null;
    if (resolver) resolver(null);
    setPickerItems([]);
  }

  // Intent parse needs the resolver. STT may be on-device (no Whisper) or audio upload.
  if (!resolverAvailable) return null;
  if (!whisperAvailable) {
    if (!preferNative) return null;
    if (!nativeChecked || !nativeReady) return null;
  }

  const state = processing ? 'processing' : recordingState;
  const isRecording = state === 'recording';

  const micTitle = props.voiceMode === 'help'
    ? (isTapMode ? 'Tap to ask a help question' : 'Hold to ask a help question')
    : (isTapMode
      ? 'Tap to speak: show, search, play, or stop'
      : 'Hold to speak: show, search, play, or stop');

  const className = 'header-voice-btn'
    + (isRecording ? ' recording' : '')
    + (state === 'processing' ? ' processing' : '')
    + (isTapMode ? ' tap-mode' : '');

  const buttonProps = isTapMode
    ? { onPointerDown: onMicPointerDown }
    : {
      onPointerDown: onMicPointerDown,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerCancel,
    };

  return (
    <>
      <span className="header-voice-wrap">
        {isTapMode && isRecording ? (
          <VoiceInputWaveform
            analyserNode={analyserNode}
            level={inputLevel}
            variant="header"
          />
        ) : null}
        <button
          type="button"
          className={className}
          aria-label={micTitle}
          title={micTitle}
          aria-pressed={isRecording}
          disabled={state === 'processing'}
          {...buttonProps}
        >
          {state === 'processing'
            ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            : <MicIcon />}
        </button>
      </span>
      <SearchResultPickerModal
        show={showPicker}
        title={pickerTitle}
        items={pickerItems}
        emptyMessage="No matching tunes."
        onSelect={handlePickerSelect}
        onHide={handlePickerHide}
      />
      <VoiceHelpAnswerModal
        show={showHelpAnswer}
        question={helpAnswer && helpAnswer.question ? helpAnswer.question : ''}
        answer={helpAnswer && helpAnswer.answer ? helpAnswer.answer : ''}
        links={helpAnswer && helpAnswer.links ? helpAnswer.links : EMPTY_HELP_LINKS}
        accessToken={props.token && props.token.access_token}
        onHide={function() { setShowHelpAnswer(false) }}
      />
    </>
  );
}
